import { createSlice } from '@reduxjs/toolkit';
import {
  serviceGetApiClients,
  servicePostApiClient,
  serviceToggleApiClient,
  serviceDeleteApiClient,
  serviceGetApiTokens,
  serviceGetApiTemplates,
  servicePostApiToken,
  serviceRevokeApiToken,
  serviceDeleteApiToken,
  serviceGetApiAuditLog,
  serviceGetProjects,
} from 'utils';

const initialState = {
  data: null,
  isLoaded: { post: false, put: false, delete: false, get: false },
  success: { post: false, put: false, delete: false, get: false },
  error:   { post: false, put: false, delete: false, get: false },
  projects: [],
  templates: [],
};

const ApiTokenSlice = createSlice({
  name: 'apiToken',
  initialState,
  reducers: {
    dataRequest(state, action) {
      return { ...state, isLoaded: { ...state.isLoaded, [action.payload]: true } };
    },
    dataSuccess(state, action) {
      return {
        ...state,
        isLoaded: { ...state.isLoaded, [action.payload.fetchType]: false },
        success:  { ...state.success,  [action.payload.fetchType]: true },
        data: action.payload.data || null,
      };
    },
    dataFailure(state, action) {
      return {
        ...state,
        isLoaded: { ...state.isLoaded, [action.payload.fetchType]: false },
        error:    { ...state.error,    [action.payload.fetchType]: action.payload.data?.message },
      };
    },
    setProjects(state, action) {
      state.projects = action.payload;
    },
    setTemplates(state, action) {
      state.templates = action.payload;
    },
    cleanUpApiToken() { return initialState; },
    cleanUpMessageApiToken(state) {
      return { ...state, data: null, isLoaded: initialState.isLoaded, success: initialState.success, error: initialState.error };
    },
  },
});

const { dataRequest, dataSuccess, dataFailure, setProjects, setTemplates, cleanUpApiToken, cleanUpMessageApiToken } = ApiTokenSlice.actions;

const fetchGetApiClients = (args) => (dispatch) => {
  dispatch(dataRequest('get'));
  serviceGetApiClients(args)
    .then((r) => dispatch(dataSuccess({ data: r.data, fetchType: 'get' })))
    .catch((e) => dispatch(dataFailure({ data: e, fetchType: 'get' })));
};

const fetchPostApiClient = (args) => (dispatch) => {
  dispatch(dataRequest('post'));
  servicePostApiClient(args)
    .then((r) => dispatch(dataSuccess({ data: r.data, fetchType: 'post' })))
    .catch((e) => dispatch(dataFailure({ data: e, fetchType: 'post' })));
};

const fetchToggleApiClient = (args) => (dispatch) => {
  dispatch(dataRequest('put'));
  serviceToggleApiClient(args)
    .then((r) => dispatch(dataSuccess({ data: r.data, fetchType: 'put' })))
    .catch((e) => dispatch(dataFailure({ data: e, fetchType: 'put' })));
};

const fetchDeleteApiClient = (args) => (dispatch) => {
  dispatch(dataRequest('delete'));
  serviceDeleteApiClient(args)
    .then((r) => dispatch(dataSuccess({ data: r.data, fetchType: 'delete' })))
    .catch((e) => dispatch(dataFailure({ data: e, fetchType: 'delete' })));
};

const fetchGetApiTokens = (args) => (dispatch) => {
  dispatch(dataRequest('get'));
  serviceGetApiTokens(args)
    .then((r) => dispatch(dataSuccess({ data: r.data, fetchType: 'get' })))
    .catch((e) => dispatch(dataFailure({ data: e, fetchType: 'get' })));
};

const fetchPostApiToken = (args) => (dispatch) => {
  dispatch(dataRequest('post'));
  servicePostApiToken(args)
    .then((r) => dispatch(dataSuccess({ data: r.data, fetchType: 'post' })))
    .catch((e) => dispatch(dataFailure({ data: e, fetchType: 'post' })));
};

const fetchRevokeApiToken = (args) => (dispatch) => {
  dispatch(dataRequest('put'));
  serviceRevokeApiToken(args)
    .then((r) => dispatch(dataSuccess({ data: r.data, fetchType: 'put' })))
    .catch((e) => dispatch(dataFailure({ data: e, fetchType: 'put' })));
};

const fetchDeleteApiToken = (args) => (dispatch) => {
  dispatch(dataRequest('delete'));
  serviceDeleteApiToken(args)
    .then((r) => dispatch(dataSuccess({ data: r.data, fetchType: 'delete' })))
    .catch((e) => dispatch(dataFailure({ data: e, fetchType: 'delete' })));
};

const fetchGetApiAuditLog = (args) => (dispatch) => {
  dispatch(dataRequest('get'));
  serviceGetApiAuditLog(args)
    .then((r) => dispatch(dataSuccess({ data: r.data, fetchType: 'get' })))
    .catch((e) => dispatch(dataFailure({ data: e, fetchType: 'get' })));
};

const fetchGetProjects = (args) => (dispatch) => {
  serviceGetProjects(args)
    .then((r) => {
      const formattedProjects = (r.data || []).map((p) => ({
        id: p.id || p.project_id,
        name: p.name || p.project_name,
      }));
      dispatch(setProjects(formattedProjects));
    })
    .catch(() => dispatch(setProjects([])));
};

const fetchGetTemplates = (args) => (dispatch) => {
  serviceGetApiTemplates(args)
    .then((r) => {
      const formattedTemplates = (r.data || []).map((t) => ({
        id: t.worktemplate_id,
        name: t.name,
        // proyect_id is required to filter the template list
        // according to the projects selected in the token dialog.
        proyect_id: t.proyect_id != null ? Number(t.proyect_id) : null,
      }));
      dispatch(setTemplates(formattedTemplates));
    })
    .catch(() => dispatch(setTemplates([])));
};

export {
  fetchGetApiClients, fetchPostApiClient, fetchToggleApiClient, fetchDeleteApiClient,
  fetchGetApiTokens, fetchPostApiToken, fetchRevokeApiToken, fetchDeleteApiToken, fetchGetApiAuditLog,
  fetchGetProjects, fetchGetTemplates,
  cleanUpApiToken, cleanUpMessageApiToken,
};

export default ApiTokenSlice.reducer;
